import template from './template.v2.json' with { type: "json" };
import packageInfo from '../package.json' with { type: "json" };
import { bigIntToVmNumber, binToHex, binToUtf8, createVirtualMachineBch2026, deriveHdPublicKey, disassembleBytecodeBch, generateTransaction, hdPrivateKeyToP2pkhLockingBytecode, hexToBin, swapEndianness, verifyTransactionTokens, hash256, decodeTransactionBch, utf8ToBin, encodeDataPush } from "@bitauth/libauth";
import { decodePushBytes, getTransactionFees, getAddress, getLibauthCompiler, getScriptHash, sumSourceOutputValue, sumOutputValue, cashAssemblyToHex } from '@unspent/tau';
import { Vault } from './vault.js';
import { toBin } from './util.js';
const BIP68_MASK = 2147483648;
export class Post {
    hash;
    auth;
    height;
    sequence;
    body;
    likes;
    dislikes;
    ref;
    error;
    constructor({ hash = "", auth = "", height = -1, sequence = -1, body = "", likes = 0, dislikes = 0, ref = "", error = "" }) {
        this.hash = hash;
        this.auth = auth;
        this.height = height;
        this.sequence = sequence > BIP68_MASK ? sequence - BIP68_MASK : sequence;
        this.body = body;
        this.likes = likes;
        this.dislikes = dislikes;
        this.ref = ref;
        this.error = error;
    }
}
export function parseUsername(commitment) {
    const commitmentBin = hexToBin(commitment);
    const commitmentAsm = disassembleBytecodeBch(commitmentBin);
    const unameHex = commitmentAsm.split(" ").pop().substring(2);
    return binToUtf8(hexToBin(unameHex));
}
function parsePostTransaction(channelLock, height, hash, transaction) {
    if (!transaction)
        return;
    let tx = decodeTransactionBch(hexToBin(transaction));
    if (typeof tx == "string")
        return new Post({ height: height, hash: hash, error: tx });
    if (!tx.outputs[0].token)
        return;
    if (!tx.outputs[0].token.nft)
        return new Post({ height: height, hash: hash, error: "no nft on first output" });
    if (!tx.outputs[0].token.nft.commitment)
        return new Post({ height: height, hash: hash, error: "no nft commitment first output" });
    let body = "";
    let ref = "";
    let like = 0;
    let dislike = 0;
    const code = tx.outputs
        .filter(o => binToHex(o.lockingBytecode) == channelLock)
        .filter(o => o.token)
        .filter(o => o.token?.nft)
        .map(o => {
        return binToHex(o.token?.nft?.commitment);
    });
    if (!code)
        return;
    if (!code[0])
        return;
    if (code[0].slice(0, 2) == "6a") {
        const payload = decodePushBytes(code[0].slice(2)).map(data => binToUtf8(data));
        if (payload[0] == "V0") {
            body = code.map(commitment => decodePushBytes(commitment.slice(2))[1]).map(bin => binToUtf8(bin)).join("");
        }
        else if (code[0].slice(0, 8) == "6a0256b2") {
            ref = code[0].slice(10);
            like = 1;
        }
        else if (payload[0] == "V,") {
            ref = code[0].slice(10);
            body = code.slice(1).map(commitment => decodePushBytes(commitment.slice(2))[1]).map(bin => binToUtf8(bin)).join("");
        }
    }
    return new Post({
        hash: hash,
        auth: binToHex(tx.outputs[0]?.token?.category),
        body: body,
        height: height,
        sequence: tx.inputs[0]?.sequenceNumber,
        ref: ref,
        likes: like,
        dislikes: dislike,
    });
}
function chunks(arr, len) {
    var chunks = [], i = 0, n = arr.length;
    while (i < n) {
        chunks.push(arr.slice(i, i += len));
    }
    return chunks;
}
const blockSort = (a, b) => {
    if (b <= 0 && a > 0) {
        return -1;
    }
    else if (b > 0 && a > 0) {
        return Math.sign(a - b);
    }
    else {
        return Math.sign(b - a);
    }
};
/**
 * Build a list of top level posts given a list of transactions
 *
 * @param prefix - cashaddress prefix.
 * @throws {Error} if transaction generation fails.
 * @returns a cashaddress.
 */
export function buildChannel(history, transactions, channel) {
    const channelLock = binToHex(Channel.getLockingBytecode(channel));
    // build a map of transactions by hash
    const transactionMap = new Map(transactions.map(tx => [swapEndianness(binToHex(hash256(hexToBin(tx)))), tx]));
    // Assuming a sorted list of transactions by block height
    let posts = history
        .map(h => {
        return parsePostTransaction(channelLock, h.height, h.tx_hash, transactionMap.get(h.tx_hash));
    })
        .filter(p => p != undefined);
    let likeOccurrences = posts.filter(p => p.likes == 1).map(p => { return p.ref; });
    let likes = likeOccurrences.reduce((acc, e) => acc.set(e, (acc.get(e) || 0) + 1), new Map());
    for (const post of posts) {
        post.likes = likes.get(post.hash) ? likes.get(post.hash) : 0;
    }
    posts = posts.sort((a, b) => {
        if (b.height === a.height) {
            return Math.sign(a.sequence - b.sequence);
        }
        else if (b.height <= 0 && a.height <= 0) {
            return Math.sign(a.sequence - b.sequence);
        }
        else
            return blockSort(a.height, b.height);
    });
    return posts.filter(p => p.body != "");
}
export class Channel {
    static USER_AGENT = packageInfo.name;
    static PROTOCOL_IDENTIFIER = "U3V";
    static tokenAware = true;
    static template = template;
    static compiler = getLibauthCompiler(this.template);
    static vm = createVirtualMachineBch2026();
    static getLockingBytecode(channel) {
        const lockingBytecodeResult = this.compiler.generateBytecode({
            data: {
                "bytecode": {
                    "channel": toBin(channel)
                }
            },
            scriptId: 'channel_lock',
        });
        if (!lockingBytecodeResult.success) {
            /* c8 ignore next */
            throw new Error('Failed to generate bytecode, script: , ' + JSON.stringify(lockingBytecodeResult, null, '  '));
        }
        return lockingBytecodeResult.bytecode;
    }
    static getScriptHash(channel, reversed = true) {
        return getScriptHash(this.getLockingBytecode(channel), reversed);
    }
    /**
     * Get cashaddress
     *
     * @param prefix - cashaddress prefix.
     * @throws {Error} if transaction generation fails.
     * @returns a cashaddress.
     */
    static getAddress(channel = "", prefix = "bitcoincash") {
        return getAddress(this.getLockingBytecode(channel), prefix, this.tokenAware);
    }
    static getInputs(channel, utxos, now, edit = false) {
        return utxos.map(u => this.getInput(channel, u, now, edit));
    }
    static getInput(channel, utxo, now, edit = false) {
        let scriptId = edit ? 'edit_message' : 'process_message';
        const sequence = (now - utxo.height) >= 1000 ? 1000 : 0;
        return {
            outpointIndex: utxo.tx_pos,
            outpointTransactionHash: hexToBin(utxo.tx_hash),
            // 
            sequenceNumber: sequence,
            unlockingBytecode: {
                compiler: this.compiler,
                script: scriptId,
                data: {
                    "bytecode": {
                        "channel": toBin(channel),
                        "locktime": bigIntToVmNumber(BigInt((Number(utxo.value) / 10) * 1000))
                    }
                },
                valueSatoshis: BigInt(utxo.value),
                token: utxo.token_data ? {
                    category: hexToBin(utxo.token_data.category),
                    amount: BigInt(utxo.token_data.amount),
                    nft: utxo.token_data.nft ? {
                        commitment: hexToBin(utxo.token_data.nft.commitment),
                        capability: utxo.token_data.nft.capability,
                    } : undefined
                } : undefined
            },
        };
    }
    static getSourceOutputs(channel, utxos) {
        return utxos.map(u => this.getSourceOutput(channel, u));
    }
    static getSourceOutput(channel, utxo) {
        return {
            outpointIndex: utxo.tx_pos,
            outpointTransactionHash: hexToBin(utxo.tx_hash),
            // 
            sequenceNumber: 1,
            unlockingBytecode: Uint8Array.from([]),
            lockingBytecode: this.getLockingBytecode(channel),
            valueSatoshis: BigInt(utxo.value),
        };
    }
    static getWalletSourceOutput(utxo, key) {
        const lockingBytecode = key ? hdPrivateKeyToP2pkhLockingBytecode({
            addressIndex: 0,
            hdPrivateKey: key
        }) : Uint8Array.from(Array(33));
        return {
            lockingBytecode: lockingBytecode,
            valueSatoshis: BigInt(utxo.value),
            token: utxo.token_data ? {
                category: hexToBin(utxo.token_data.category),
                amount: BigInt(utxo.token_data.amount),
                nft: utxo.token_data.nft ? {
                    commitment: hexToBin(utxo.token_data.nft.commitment),
                    capability: utxo.token_data.nft.capability,
                } : undefined
            } : undefined
        };
    }
    static getCouponOutputs(utxos, now) {
        return utxos.map(u => this.getCouponOutput(u, now)).filter(u => u !== undefined);
    }
    static getCouponOutput(utxo, now) {
        let futureTime = Math.floor(utxo.value / 10) * 1000;
        // if the utxo was underfunded, clear it without generating a coupon.
        let isSpam = ((futureTime - utxo.height) < 1000);
        let isPremature = (now - utxo.height) < 1000;
        let outputValue = isPremature ? utxo.value * 10 : utxo.value;
        // A non matching output value indicates the message is spam
        outputValue = isSpam ? utxo.value - 1 : outputValue;
        let couponThreshold = isPremature ? 100_000_000 : 10_000_000;
        couponThreshold = isSpam ? 10_000_000 : couponThreshold;
        let bytecode = Vault.getCouponLockingBytecode(couponThreshold, futureTime);
        return {
            lockingBytecode: bytecode,
            valueSatoshis: BigInt(outputValue),
        };
    }
    static getWalletInputs(utxos, key, sequence) {
        return utxos.map((u) => this.getWalletInput(u, key, sequence));
    }
    static getWalletInput(utxo, privateKey, sequence, addressIndex = 0) {
        let unlockingData = privateKey ? {
            compiler: this.compiler,
            data: {
                hdKeys: {
                    addressIndex: addressIndex,
                    hdPrivateKeys: {
                        'wallet': privateKey
                    },
                }
            },
            script: 'wallet_unlock',
            valueSatoshis: BigInt(utxo.value),
        } : Uint8Array.from(Array());
        //@ts-ignore
        unlockingData.token = utxo.token_data ? {
            category: hexToBin(utxo.token_data.category),
            amount: utxo.token_data.amount,
            nft: utxo.token_data.nft ? {
                commitment: hexToBin(utxo.token_data.nft.commitment),
                capability: utxo.token_data.nft.capability,
            } : undefined
        } : undefined;
        return {
            outpointIndex: utxo.tx_pos,
            outpointTransactionHash: hexToBin(utxo.tx_hash),
            sequenceNumber: sequence ? sequence + BIP68_MASK : sequence,
            unlockingBytecode: unlockingData,
        };
    }
    static getChannelMessageOutputs(channel, message, auth, couponValue) {
        const binaryMessage = utf8ToBin(message);
        let chunked = [...chunks(binaryMessage, 122).map((m) => "6a025630" + binToHex(encodeDataPush(m)))];
        return chunked.map((m) => {
            return {
                lockingBytecode: this.getLockingBytecode(channel),
                valueSatoshis: BigInt(couponValue + Math.floor(Math.random() * 100)),
                token: {
                    amount: 0n,
                    category: hexToBin(auth.token_data.category),
                    nft: {
                        capability: 'mutable',
                        commitment: hexToBin(m)
                    }
                }
            };
        });
    }
    static getLikeOutput(channel, postId, auth, couponValue) {
        let m = "6a02562b" + binToHex(encodeDataPush(hexToBin(postId)));
        console.log(BigInt(couponValue + Math.floor(Math.random() * 100)));
        return {
            lockingBytecode: this.getLockingBytecode(channel),
            valueSatoshis: BigInt(couponValue + Math.floor(Math.random() * 100)),
            token: {
                amount: 0n,
                category: hexToBin(auth.token_data.category),
                nft: {
                    capability: 'none',
                    commitment: hexToBin(m)
                }
            }
        };
    }
    static getReplyOutput(channel, postId, message, auth, couponValue) {
        let m = cashAssemblyToHex('OP_RETURN <"V,">') + binToHex(encodeDataPush(hexToBin(postId)));
        let ref = {
            lockingBytecode: this.getLockingBytecode(channel),
            valueSatoshis: BigInt(couponValue),
            token: {
                amount: 0n,
                category: hexToBin(auth.token_data.category),
                nft: {
                    capability: 'none',
                    commitment: hexToBin(m)
                }
            }
        };
        const binaryMessage = utf8ToBin(message);
        let chunked = [...chunks(binaryMessage, 122).map((m) => cashAssemblyToHex('OP_RETURN <"V0">') + binToHex(encodeDataPush(m)))];
        return [
            ref,
            ...chunked.map((m) => {
                return {
                    lockingBytecode: this.getLockingBytecode(channel),
                    valueSatoshis: BigInt(couponValue),
                    token: {
                        amount: 0n,
                        category: hexToBin(auth.token_data.category),
                        nft: {
                            capability: 'mutable',
                            commitment: hexToBin(m)
                        }
                    }
                };
            })
        ];
    }
    static getChangeOutput(auth, changeAmount, privateKey, addressIndex = 0) {
        const lockingBytecode = privateKey ? {
            compiler: this.compiler,
            data: {
                hdKeys: {
                    addressIndex: addressIndex,
                    hdPublicKeys: {
                        'wallet': deriveHdPublicKey(privateKey).hdPublicKey
                    },
                },
            },
            script: 'wallet_lock'
        } : Uint8Array.from(Array(33));
        return {
            lockingBytecode: lockingBytecode,
            valueSatoshis: changeAmount,
            token: {
                amount: 0n,
                category: hexToBin(auth.token_data.category),
                nft: {
                    capability: "minting",
                    commitment: hexToBin(auth.token_data?.nft?.commitment)
                }
            }
        };
    }
    /**
     * Clear a channel message.
     *
     * Messages may be cleared based on different conditions:
     *
     * 1. Messages that are older than 1000 blocks can be turned into 10M coupons
     * 1. Messages that are not older than 1000 blocks can be into 100M coupons
     * 1. Messages that were insufficiently funded may be taken at will without generating a coupon.
     *
     * @param channel - channel identifier.
     * @param utxos - utxos held by the channel to drop.
     * @param auth - utxo paying transaction fees.
     * @param key - private key to sign transaction wallet inputs.
     * @param now - current timestamp, in blocks.
     * @param extraUtxos - wallet utxos for extra fees.
     * @param fee - network fee to pay, default 1 sat per byte.
     *
     * @throws {Error} if transaction generation fails.
     * @returns a transaction template.
     */
    static clear(channel, utxos, auth, key, now, extraUtxos, fee = 1) {
        const inputs = [];
        const outputs = [];
        const sourceOutputs = [];
        let config = {
            locktime: now,
            version: 2,
            inputs,
            outputs
        };
        let walletUtxos = [auth];
        if (extraUtxos)
            walletUtxos.push(...extraUtxos);
        config.inputs.push(...this.getInputs(channel, utxos, now));
        config.inputs.push(...this.getWalletInputs(walletUtxos, key));
        config.outputs.push(...this.getCouponOutputs(utxos, now));
        sourceOutputs.push(...this.getSourceOutputs(channel, utxos));
        sourceOutputs.push(this.getWalletSourceOutput(auth, key));
        let valueIn = sumSourceOutputValue(sourceOutputs);
        let change = valueIn - sumOutputValue(config.outputs);
        config.outputs.push(this.getChangeOutput(auth, change, key));
        return this.buildAndValidateTransaction(config, sourceOutputs, fee);
    }
    /**
     * Create (or edit) a post.
     *
     * @param channel - channel identifier.
     * @param message - full post message.
     * @param auth - utxo paying transaction fees.
     * @param couponAmount - amount to pay message (per commitment).
     * @param key - private key to sign transaction wallet inputs.
     * @param prevUtxos - post utxos to edit.
     * @param fee - network fee to pay, default 1 sat per byte.
     *
     * @throws {Error} if transaction generation fails.
     * @returns a transaction template.
     */
    static post(channel, message, auth, couponAmount, key, sequence, prevUtxos, fee = 1) {
        const inputs = [];
        const outputs = [];
        const sourceOutputs = [];
        let config = {
            locktime: 0,
            version: 2,
            inputs,
            outputs
        };
        let walletUtxos = [auth];
        if (prevUtxos)
            walletUtxos.push(...prevUtxos);
        config.inputs.push(...this.getWalletInputs(walletUtxos, key, sequence));
        config.outputs.push(...this.getChannelMessageOutputs(channel, message, auth, couponAmount));
        sourceOutputs.push(this.getWalletSourceOutput(auth, key));
        let valueIn = sumSourceOutputValue(sourceOutputs);
        let change = valueIn - sumOutputValue(config.outputs);
        config.outputs.push(this.getChangeOutput(auth, change, key));
        return this.buildAndValidateTransaction(config, sourceOutputs, fee);
    }
    /**
    * Like a post.
    *
    * @param channel - channel identifier.
    * @param postId - transaction id of the post being liked.
    * @param auth - utxo paying transaction fees.
    * @param couponAmount - amount to pay message (per commitment).
    * @param key - private key to sign transaction wallet inputs.
    * @param fee - network fee to pay, default 1 sat per byte.
    *
    * @throws {Error} if transaction generation fails.
    * @returns a transaction template.
    */
    static like(channel, postId, auth, couponAmount, key, fee = 1) {
        const inputs = [];
        const outputs = [];
        const sourceOutputs = [];
        let config = {
            locktime: 0,
            version: 2,
            inputs,
            outputs
        };
        let walletUtxos = [auth];
        config.inputs.push(...this.getWalletInputs(walletUtxos, key));
        config.outputs.push(this.getLikeOutput(channel, postId, auth, couponAmount));
        sourceOutputs.push(this.getWalletSourceOutput(auth, key));
        let valueIn = sumSourceOutputValue(sourceOutputs);
        let change = valueIn - sumOutputValue(config.outputs);
        config.outputs.push(this.getChangeOutput(auth, change, key));
        return this.buildAndValidateTransaction(config, sourceOutputs, fee);
    }
    /**
    * Reply to a post.
    *
    * @param channel - channel identifier.
    * @param postId - transaction id of the post being liked.
    * @param message - reply message.
    * @param auth - utxo paying transaction fees.
    * @param couponAmount - amount to pay message (per commitment).
    * @param key - private key to sign transaction wallet inputs.
    * @param fee - network fee to pay, default 1 sat per byte.
    *
    * @throws {Error} if transaction generation fails.
    * @returns a transaction template.
    */
    static reply(channel, postId, message, auth, couponAmount, key, fee = 1) {
        const inputs = [];
        const outputs = [];
        const sourceOutputs = [];
        let config = {
            locktime: 0,
            version: 2,
            inputs,
            outputs
        };
        let walletUtxos = [auth];
        config.inputs.push(...this.getWalletInputs(walletUtxos, key));
        config.outputs.push(...this.getReplyOutput(channel, postId, message, auth, couponAmount));
        sourceOutputs.push(this.getWalletSourceOutput(auth, key));
        let valueIn = sumSourceOutputValue(sourceOutputs);
        let change = valueIn - sumOutputValue(config.outputs);
        ;
        config.outputs.push(this.getChangeOutput(auth, change, key));
        return this.buildAndValidateTransaction(config, sourceOutputs, fee);
    }
    static buildAndValidateTransaction(config, sourceOutputs, fee = 1) {
        let result = generateTransaction(config);
        if (!result.success)
            throw new Error('generate transaction failed!, errors: ' + JSON.stringify(result.errors, null, '  '));
        const estimatedFee = getTransactionFees(result.transaction, fee);
        console.log(estimatedFee);
        const lastIdx = config.outputs.length - 1;
        config.outputs[lastIdx].valueSatoshis = config.outputs[lastIdx].valueSatoshis - estimatedFee;
        result = generateTransaction(config);
        if (!result.success)
            throw new Error('generate transaction failed!, errors: ' + JSON.stringify(result.errors, null, '  '));
        const transaction = result.transaction;
        const tokenValidationResult = verifyTransactionTokens(transaction, sourceOutputs, { maximumTokenCommitmentLength: 128 });
        if (tokenValidationResult !== true && fee > 0)
            throw tokenValidationResult;
        let verify = this.vm.verify({
            sourceOutputs: sourceOutputs,
            transaction: transaction,
        });
        if (typeof verify == "string")
            throw Error(verify);
        return {
            sourceOutputs: sourceOutputs,
            transaction: transaction,
            verify: verify
        };
    }
}
//# sourceMappingURL=channel.js.map